import { RefObject, useEffect } from 'react';
import { EconsultUtils } from '@oh-vcp/components-common';

const findScrollableAncestor = (element: HTMLElement | null): HTMLElement | null => {
  let node = element?.parentElement || null;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const useSafariHitTestFix = (contentRef: RefObject<HTMLElement>) => {
  useEffect(() => {
    if (!EconsultUtils.isSafari()) {
      return undefined;
    }
    const content = contentRef.current;
    if (!content) {
      return undefined;
    }

    let frame = 0;
    const nudge = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const scrollable = findScrollableAncestor(content);
        if (!scrollable) {
          return;
        }
        const max = scrollable.scrollHeight - scrollable.clientHeight;
        if (max <= 0) {
          return;
        }
        const original = scrollable.scrollTop;
        const delta = original >= max ? -1 : 1;
        scrollable.scrollTop = original + delta;
        window.requestAnimationFrame(() => {
          scrollable.scrollTop = original;
        });
      });
    };

    const resizeObserver = new ResizeObserver(nudge);
    resizeObserver.observe(content);
    window.addEventListener('resize', nudge);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      window.removeEventListener('resize', nudge);
    };
  }, [contentRef]);
};

export default useSafariHitTestFix;
